import { TellescopeModelFromName, useTellescopeMutation, useTellescopeQueryClient } from "@tellescope/tanstack-query";
import { BlockProps, ContactDetailsBlockInfoRegistry, ContactDetailsBlockType } from "../types";
import { useCallback } from "react";
import { resolveBlockRef } from "../utils";
import { useCreateBlocks } from "./use-create-blocks";

export interface UseUpdateBlockArgs<T extends ContactDetailsBlockType> {
  type: T;
  viewId: string;
  blockId?: string;
  blockIndex: number;
  onViewMaterialized?: BlockProps["onViewMaterialized"];
}

export const useBlockMutations = <T extends ContactDetailsBlockType>({
  viewId,
  blockId,
  blockIndex,
  onViewMaterialized,
}: UseUpdateBlockArgs<T>) => {
  const { getQueryData } = useTellescopeQueryClient();

  const updateViewMutation = useTellescopeMutation({
    model: "custom_dashboards",
    method: "updateOne",
    optimisticUpdates: true,
    autoCacheUpdateStrategy: "update",
  });

  /**
   * The target block and the blocks on either side of it, read from the query cache at call time.
   *
   * Every write below uses `replaceObjectFields: true` — replacing the whole blocks array — so a
   * payload rebuilt from a stale snapshot silently reverts whatever a sibling block just saved. The
   * cache is the only source that is current mid-await; a value captured before an `await` is not.
   * Callers must therefore resolve *after* any await, never before, and build the payload from the
   * `target` returned here rather than from the render-time `block`.
   *
   * Returns `undefined` when the view is uncached or the target block is no longer present (a sibling
   * removed it while we waited) — there is no authoritative base to build a write on.
   */
  const resolveTargetAndSiblings = useCallback(() => {
    const latestBlocks = getQueryData({ model: "custom_dashboards", method: "getOne", request: viewId })?.blocks;
    const resolved = resolveBlockRef({ blocks: latestBlocks, blockId, blockIndex });
    const targetIndex = resolved.index;
    if (!latestBlocks || targetIndex === -1) return undefined;

    if (blockId !== undefined && resolved.block?.info?.id !== blockId) return undefined;

    const target = latestBlocks[targetIndex];
    if (!target) return undefined;

    return {
      target,
      frontBlocks: latestBlocks.slice(0, targetIndex),
      endBlocks: latestBlocks.slice(targetIndex + 1),
    };
  }, [blockId, blockIndex, getQueryData, viewId]);

  type MutateOptions = Parameters<typeof updateViewMutation.mutateAsync>[1];

  // TODO: [bb] rate-limit config writes via a debounce. A previous debounce here was removed because
  // its buffered commit resolved its base before awaiting and could revert a sibling block's config;
  // any reintroduction has to resolve the blocks array *after* the await, and coordinate the
  // concurrent flush every mounted block performs on tab-hide/unmount. Every current caller commits
  // on a discrete action (Enter, filter applied, toggle) or debounces locally first, so writes are
  // already coarse.
  const updateInfo = useCallback(
    (newInfo: ContactDetailsBlockInfoRegistry[T], options?: MutateOptions) => {
      const resolved = resolveTargetAndSiblings();
      if (!resolved) return;

      const updatedBlock: TellescopeModelFromName<"custom_dashboards">["blocks"][number] = {
        ...resolved.target,
        info: newInfo,
      };

      return updateViewMutation.mutateAsync(
        {
          id: viewId,
          blocks: [...resolved.frontBlocks, updatedBlock, ...resolved.endBlocks],
          _options: {
            replaceObjectFields: true,
          },
        },
        options
      );
    },
    [resolveTargetAndSiblings, updateViewMutation, viewId]
  );

  const moveToIndex = useCallback(
    async (newIndex: number, options?: MutateOptions) => {
      const resolved = resolveTargetAndSiblings();
      if (!resolved) return;

      const newBlocks = [...resolved.frontBlocks, ...resolved.endBlocks];
      newBlocks.splice(newIndex, 0, resolved.target);

      return updateViewMutation.mutateAsync(
        {
          id: viewId,
          blocks: newBlocks,
          _options: {
            replaceObjectFields: true,
          },
        },
        options
      );
    },
    [resolveTargetAndSiblings, updateViewMutation, viewId]
  );

  const duplicateBlock = useCallback(
    async (options?: MutateOptions) => {
      const resolved = resolveTargetAndSiblings();
      if (!resolved) return;

      const newBlocks = [
        ...resolved.frontBlocks,
        resolved.target,
        {
          ...resolved.target,
          info: {
            ...resolved.target.info,
            id: crypto.randomUUID(),
          },
        },
        ...resolved.endBlocks,
      ];

      return updateViewMutation.mutateAsync(
        {
          id: viewId,
          blocks: newBlocks,
          _options: {
            replaceObjectFields: true,
          },
        },
        options
      );
    },
    [resolveTargetAndSiblings, updateViewMutation, viewId]
  );

  const deleteBlock = useCallback(
    async (options?: MutateOptions) => {
      const resolved = resolveTargetAndSiblings();
      if (!resolved) return;

      const newBlocks = [...resolved.frontBlocks, ...resolved.endBlocks];

      return updateViewMutation.mutateAsync(
        {
          id: viewId,
          blocks: newBlocks,
          _options: {
            replaceObjectFields: true,
          },
        },
        options
      );
    },
    [resolveTargetAndSiblings, updateViewMutation, viewId]
  );

  const toggleExpanded = useCallback(
    async (options?: MutateOptions) => {
      const resolved = resolveTargetAndSiblings();
      if (!resolved) return;

      const updatedBlock: TellescopeModelFromName<"custom_dashboards">["blocks"][number] = {
        ...resolved.target,
        info: {
          ...resolved.target.info,
          expanded: !resolved.target.info?.expanded,
        },
      };

      return updateViewMutation.mutateAsync(
        {
          id: viewId,
          blocks: [...resolved.frontBlocks, updatedBlock, ...resolved.endBlocks],
          _options: {
            replaceObjectFields: true,
          },
        },
        options
      );
    },
    [resolveTargetAndSiblings, updateViewMutation, viewId]
  );

  const createBlocks = useCreateBlocks({ viewId, onViewMaterialized });

  return {
    updateInfo,
    moveToIndex,
    duplicateBlock,
    deleteBlock,
    toggleExpanded,
    createBlocks,
  };
};
